<template>
    <div :class="cellClass" class="sku-container">
        <span v-if="isItemLoading" class="pr-1 text-purple">
            <i class="fas fa-spinner fa-pulse"></i>
        </span>
        <span v-else-if="isMatchedItem"
              v-tooltip="'View item'"
              @click.stop.prevent="viewItem"
              key="matched"
              class="text-small text-primary-light pr-1 clickable">
            <i class="fal fa-external-link-alt"></i>
        </span>
        <span v-else-if="isAlternativeItem"
              @click.stop.prevent="viewItem"
              v-tooltip="`This item is an alternative for an existing item: ${line.original_sku}`"
              key="alternative"
              class="text-small text-primary-light pr-1">
            <i class="fal fa-code-branch"></i>
        </span>

        <span v-if="isNewItem"
              v-tooltip="`
                  ${newItemTooltip}</br>
                  If this item already exists you can select it by searching in the item code field.
              `"
              class="pr-1">
            <i class="fal fa-info-circle"></i>
        </span>
        {{ params.value }}
    </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import {checkIsAlternativeLine, checkIsNewLine} from '@/utils/document'
import {fetchItemById} from '@/api/document/items'
import {docSystemTypesDefaultModels} from '@/types/document/types'

export default Vue.extend({
    name: 'AgGridCellRendererStockCode',
    data() {
        return {
            isItemLoading: false
        }
    },
    computed: {
        ...mapGetters([
            'isNexusSandboxTeam',
            'activeTeamId'
        ]),

        ...mapGetters('doc_viewer', [
            'lines'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'validateNoNewLineItems'
        ]),

        lineIdx() {
            return this.params.data ? this.params.data.lineIdx : -1
        },

        line() {
            return this.lines[this.lineIdx] || {}
        },

        id() {
            return `sku-${this.lineIdx}`
        },

        itemId() {
            return this.line.item_uuid
        },

        isMatchedItem() {
            return !this.isAlternativeItem && this.itemId
        },

        isAlternativeItem() {
            return checkIsAlternativeLine(this.line)
        },

        isNewItem() {
            return checkIsNewLine(this.line)
        },

        newItemTooltip() {
            if (this.validateNoNewLineItems) {
                return `This document type is not configured to create new items.`
            } else {
                return `This is a new item and will be created on approval.`
            }
        },

        cellClass() {
            if (this.isNewItem) {
                if (this.validateNoNewLineItems) {
                    return 'sku-error'
                }
                return 'sku-warning'
            }
            return ''
        }
    },
    methods: {
        viewItem() {
            if (this.isNexusSandboxTeam) {
                this.openNexusTeamItem()
            } else {
                this.openItemViewer()
            }
        },

        async fetchItem() {
            this.isItemLoading = true
            const item = await fetchItemById(this.itemId)
            this.isItemLoading = false
            return item
        },

        async openNexusTeamItem() {
            const item = await this.fetchItem()
            const documentId = _.get(item, 'custom.document')
            if (!documentId) {
                this.openItemViewer()
                return
            }
            const routeData = this.$router.resolve({
                name: `${docSystemTypesDefaultModels.items.route}.view`,
                params: {
                    team: this.activeTeamId,
                    docUuid: documentId
                }
            })
            window.open(routeData.href, '_blank')
        },

        openItemViewer() {
            this.$modal.show('edit-item', {uuid: this.itemId})
        }
    }
})
</script>

<style lang="scss" scoped>
.sku-container {
    display: flex;
    align-items: center;
    padding: 0 8px;
}

.sku-warning {
    border-left: 3px solid darkorange;
    color: darkorange;
    font-weight: 500;
}

.sku-error {
    border-left: 3px solid $color-danger;
    color: $color-danger;
    font-weight: 500;
}
</style>
